import {
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  inject,
  OnDestroy
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Subscription } from 'rxjs';
import { IconService } from './services';

/**
 * Reusable icon component that displays SVG icons.
 *
 * Features:
 * - Bundled critical icons load instantly (no HTTP request)
 * - Non-critical icons load via HTTP with caching *
 */
@Component({
  selector: 'app-icon',
  standalone: true,
  imports: [CommonModule],
  template: `
    <span
      class="app-icon"
      [ngClass]="sizePreset !== 'custom' ? sizePreset : null"
      [style.width]="customWidth"
      [style.height]="customHeight"
      [innerHTML]="svgContent"
      [attr.aria-label]="ariaLabel || null"
      [attr.role]="ariaLabel ? 'img' : null"
      [attr.aria-hidden]="!ariaLabel ? true : null"
    ></span>
  `,
  styleUrls: ['./icon.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AppIconComponent implements OnChanges, OnDestroy {
  @Input() name!: string;
  @Input() size: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | string = 'md';
  @Input() width: string = '';
  @Input() height: string = '';
  @Input() color: string = '';
  @Input() fill: string = '';
  @Input() stroke: string = '';
  @Input() ariaLabel: string = '';

  svgContent: SafeHtml = '';
  sizePreset: string = 'md';
  customWidth: string | null = null;
  customHeight: string | null = null;

  private readonly iconService = inject(IconService);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly cdr = inject(ChangeDetectorRef);

  private subscription: Subscription | null = null;
  private lastLoadedSvg: string | null = null;

  private static readonly SIZE_PRESETS = ['xs', 'sm', 'md', 'lg', 'xl'];
  private static readonly COLOR_REGEX =
    /^(#[0-9A-Fa-f]{3,8}|rgb\(\d{1,3},\s*\d{1,3},\s*\d{1,3}\)|[a-zA-Z]+)$/;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['size'] || changes['width'] || changes['height']) {
      this.parseSize();
    }

    if (changes['name'] && this.name) {
      this.loadSvg();
    } else if (
      (changes['fill'] || changes['stroke'] || changes['color']) &&
      this.name
    ) {
      this.reprocessSvg();
    }
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  private parseSize(): void {
    if (this.width || this.height) {
      this.sizePreset = 'custom';
      this.customWidth = this.width || this.height || null;
      this.customHeight = this.height || this.width || null;
    } else if (AppIconComponent.SIZE_PRESETS.includes(this.size)) {
      this.sizePreset = this.size;
      this.customWidth = null;
      this.customHeight = null;
    } else {
      this.sizePreset = 'custom';
      this.customWidth = this.size;
      this.customHeight = this.size;
    }
  }

  private loadSvg(): void {
    const safeName = this.sanitizeName(this.name);

    // Cancel any pending request
    this.subscription?.unsubscribe();

    // Use IconService to get icon (checks bundled first, then HTTP)
    this.subscription = this.iconService.getIcon(safeName).subscribe({
      next: (result) => {
        if (result.svg) {
          this.lastLoadedSvg = result.svg;
          this.applySvg(result.svg);
        }
      },
      error: (err) => {
        console.error(`Failed to load icon: ${this.name}`, err);
      }
    });
  }

  private applySvg(svg: string): void {
    const processedSvg = this.applyColorsToSvg(svg);
    this.svgContent = this.sanitizer.bypassSecurityTrustHtml(processedSvg);
    this.cdr.markForCheck();
  }

  private reprocessSvg(): void {
    if (this.lastLoadedSvg) {
      this.applySvg(this.lastLoadedSvg);
    }
  }

  private applyColorsToSvg(svg: string): string {
    let result = svg;

    if (this.color && this.validateColor(this.color)) {
      result = result.replace(/fill="(?!none)[^"]*"/g, `fill="${this.color}"`);
      result = result.replace(/stroke="[^"]*"/g, `stroke="${this.color}"`);
    }

    if (this.fill && this.validateColor(this.fill)) {
      result = result.replace(/fill="(?!none)[^"]*"/g, `fill="${this.fill}"`);
    }

    if (this.stroke && this.validateColor(this.stroke)) {
      result = result.replace(/stroke="[^"]*"/g, `stroke="${this.stroke}"`);
    }

    return result;
  }

  private validateColor(color: string): boolean {
    return AppIconComponent.COLOR_REGEX.test(color);
  }

  private sanitizeName(name: string): string {
    return name.replace(/[^a-zA-Z0-9_-]/g, '');
  }
}
